import { FlyoutContainer, Tab, TabFields, TabsType } from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoanInventoryTableFields } from '../../types';
import styles from './styles.module.scss';
import HistoricalData from './HistoricalData';
import HistoricalHeader from './HistoricalHeader';
import { formatDateTime } from 'Feature/V2/Shared_Portfolio/utils/formatDateTime';
import { useDecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/hooks/useDecisionOverrideRationales';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import { LoanInventoryTableFields as PortfolioLoanInventoryTableFields } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { LoanDetails } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import {
  REASON_FOR_PASSING_LOAN_NON_CANCELLATION,
  REASON_FOR_FAILING_LOAN_NON_CANCELLATION,
  REASON_FOR_PASSING_LOAN_CANCELLATION,
  REASON_FOR_FAILING_LOAN_CANCELLATION
} from 'Feature/V2/HbtMasterPool/constants';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';

interface HistoricalDataFlyoutProps {
  openFlyout: boolean;
  toggleFlyout: (event: React.KeyboardEvent | React.MouseEvent) => void;
  selectedRowsData:
    | LoanInventoryTableFields[]
    | PortfolioLoanInventoryTableFields[]
    | LoanDetails[];
  isItMasterPoolLoans?: boolean;
  isItLoanDetails?: boolean;
}

const HistoricalDataFlyout = ({
  openFlyout,
  toggleFlyout,
  selectedRowsData,
  isItMasterPoolLoans = true,
  isItLoanDetails = false
}: HistoricalDataFlyoutProps) => {
  const { t } = useTranslation('HbtMasterPool');
  const authenticationContext = useAuthenticationContext();
  const [loanInventoryError, setLoanInventoryError] = useState<string>('');

  const {
    data: rationaleResponse,
    isLoading: isRationaleLoading,
    isError: isRationaleError
  } = useDecisionOverrideRationales(authenticationContext);

  const renderHistoryForMasterPoolLoans = (data?: LoanInventoryTableFields) => {
    if (!data || !data.remarks) return <></>;
    let previousStatus: string = data.originalStatus ?? '';

    const rowData = data.remarks.map((d, index) => {
      const remarkForCurrentStatus = data.remarks[index];
      const match = rationaleResponse?.find(
        (rationale: any) => rationale.id === data.remarks[index].remarkId
      );
      const timestamp = formatDateTime(d.recordCreatedTimestamp ?? '');
      const dateStamp = timestamp?.date ?? '';
      const timeStamp = timestamp?.time ?? '';
      let status = '';
      if (data.statusHistory && data.statusHistory.length > 0) {
        const matchingStatus = data.statusHistory.find(
          (status) => status.recordCreatedTimestamp === d.recordCreatedTimestamp
        );

        if (matchingStatus) {
          status = matchingStatus.originalAccountStatusTypeCode ?? '';
        }
      }
      let header = Boolean(status) ? t('Status-Changed') : t('Remarks-Added');
      previousStatus = status;
      const editor = `${t('Editor')}: ${data.remarks[index]?.lastName ?? 'No lastname'}, ${data.remarks[index]?.firstName ?? 'No firstname'}`;

      let remark = i18n.language === 'en' ? match?.englishRationale : match?.frenchRationale;

      if (
        isRemarkOther({
          remarkID: data.remarks[index].remarkId,
          remarkMessage: data.remarks[index].remarkText
        })
      ) {
        remark = remarkForCurrentStatus.remarkText;
      }

      return (
        <HistoricalData
          header={header}
          status={Boolean(status) ? status : undefined}
          editor={editor}
          remark={remark}
          timeStamp={timeStamp}
          dateStamp={dateStamp}
        />
      );
    });

    const initialLoan = (
      <HistoricalData
        header={t('Initial-Status')}
        status={data.originalStatus}
        timeStamp={''}
        dateStamp={''}
      />
    );

    return [...rowData, initialLoan];
  };

  const renderHistoryForLoanInventoryLoans = (
    data?: PortfolioLoanInventoryTableFields | LoanDetails
  ) => {
    if (!data || !data.remarks) return <></>;

    const rowData = data?.remarks?.map((d) => {
      const timestamp = formatDateTime(d.recordUpdatedTimestamp ?? d.recordCreatedTimestamp ?? '');
      const dateStamp = timestamp?.date ?? '';
      const timeStamp = timestamp?.time ?? '';
      const editor = `${t('Editor')}: ${d.lastName}, ${d.firstName}`;
      const match = rationaleResponse?.find((rationale: any) => rationale.id === d.remarkId);

      let header = '';
      switch (true) {
        case REASON_FOR_PASSING_LOAN_NON_CANCELLATION.includes(match?.id!):
        case REASON_FOR_PASSING_LOAN_CANCELLATION.includes(match?.id!):
          header = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Reason-For-Passing-Loan');
          break;

        case REASON_FOR_FAILING_LOAN_NON_CANCELLATION.includes(match?.id!):
        case REASON_FOR_FAILING_LOAN_CANCELLATION.includes(match?.id!):
          header = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Reason-For-Failing-Loan');
          break;

        case isRemarkOther({ remarkID: match?.id, remarkMessage: d.remarkText }):
          header = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other');
          break;

        default:
          header = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other');
      }

      let remark = i18n.language === 'en' ? match?.englishRationale : match?.frenchRationale;

      if (isRemarkOther({ remarkID: d.remarkId, remarkMessage: d.remarkText })) {
        remark = d.remarkText;
      }

      return (
        <HistoricalData
          header={header}
          remark={remark}
          timeStamp={timeStamp}
          dateStamp={dateStamp}
          editor={editor}
        />
      );
    });

    return [...rowData];
  };

  let selectedDataWithHistoricalData:
    | LoanInventoryTableFields[]
    | PortfolioLoanInventoryTableFields[]
    | LoanDetails[] = [];

  if (isItMasterPoolLoans) {
    const masterRows = selectedRowsData as LoanInventoryTableFields[];
    selectedDataWithHistoricalData = masterRows.filter(
      (row): row is LoanInventoryTableFields => row.remarks.length > 0
    );
  } else if (!isItMasterPoolLoans && !isItLoanDetails) {
    const portfolioRows = selectedRowsData as PortfolioLoanInventoryTableFields[];
    selectedDataWithHistoricalData = portfolioRows.filter(
      (row): row is PortfolioLoanInventoryTableFields =>
        Array.isArray(row.remarks) && row.remarks.length > 0
    );
  } else if (isItLoanDetails) {
    const loandDetails = selectedRowsData as LoanDetails[];
    selectedDataWithHistoricalData = loandDetails.filter(
      (row): row is LoanDetails => Array.isArray(row.remarks) && row.remarks.length > 0
    );
  }

  const tabItems: TabFields[] = selectedDataWithHistoricalData.map((data) => {
    return {
      label: {
        value:
          isItMasterPoolLoans && 'cmhcAccountNumber' in data
            ? data.cmhcAccountNumber
            : 'cmhcAccountNumber' in data && data.cmhcAccountNumber
              ? data.cmhcAccountNumber
              : data.lenderReferenceNumber
      },
      content: (
        <>
          {isItMasterPoolLoans
            ? renderHistoryForMasterPoolLoans(data as LoanInventoryTableFields)
            : renderHistoryForLoanInventoryLoans(data)}
        </>
      )
    };
  });

  useEffect(() => {
    if (isRationaleError) {
      setLoanInventoryError('api_error');
    } else {
      setLoanInventoryError('');
    }
  }, [isRationaleError]);

  return (
    <Box>
      <FlyoutContainer
        id={styles.HistoricalFlyout}
        isHeaderRequired={false}
        open={openFlyout}
        onClose={toggleFlyout}
        content={
          <Box className={styles.HistoricalDataFlyout}>
            <div className={styles.stickyHeader}>
              <HistoricalHeader
                data={selectedDataWithHistoricalData}
                toggleFlyout={toggleFlyout}
                isItMasterPoolLoans={isItMasterPoolLoans}
              />
            </div>
            <div className={styles.scrollRegion}>
              {isRationaleLoading && (
                <Box className={styles.spinnerElementWrapper}>
                  <SpinnerElement />
                </Box>
              )}
              {!isRationaleLoading && isRationaleError && loanInventoryError && (
                <Box
                  sx={{
                    padding: '32px;'
                  }}
                >
                  <ErrorMessage errorType={loanInventoryError} />
                </Box>
              )}
              {!isRationaleLoading &&
                !isRationaleError &&
                (selectedDataWithHistoricalData.length > 1 ? (
                  <Tab tabs={tabItems} tabsType={TabsType.FULL_WIDTH} tabWidthAuto />
                ) : isItMasterPoolLoans ? (
                  renderHistoryForMasterPoolLoans(
                    (selectedDataWithHistoricalData[0] as LoanInventoryTableFields) ?? undefined
                  )
                ) : (
                  renderHistoryForLoanInventoryLoans(selectedDataWithHistoricalData[0] ?? undefined)
                ))}
            </div>
          </Box>
        }
      />
    </Box>
  );
};

export default memo(HistoricalDataFlyout);
